import { useEffect, useState } from 'react';

import { Suggestion, SearchForm } from '../types';

const useSuggestionsManager = ({
  isDepartureOpen,
  isDestinationOpen,
  departureInput,
  destinationInput,
  defaultDepartureSuggestions,
  defaultDestinationSuggestions,
  departureSuggestions,
  destinationSuggestions,
  destinationSuggestionsByDepartureId,
  departureId,
}: {
  isDepartureOpen: boolean;
  isDestinationOpen: boolean;
  departureInput?: SearchForm['departure'];
  destinationInput?: SearchForm['destination'];
  defaultDepartureSuggestions: Suggestion[];
  defaultDestinationSuggestions: Suggestion[];
  departureSuggestions: Suggestion[];
  destinationSuggestions: Suggestion[];
  destinationSuggestionsByDepartureId: Suggestion[];
  departureId?: SearchForm['departureOption']['value'];
}) => {
  const [departureSuggestionsToShow, setDepartureSuggestsToShow] = useState<Suggestion[]>([]);
  const [destinationSuggestionsToShow, setDestinationSuggestsToShow] = useState<Suggestion[]>([]);

  const [isDepartureInEditMode, setIsDepartureInEditMode] = useState(false);
  const [isDestinationInEditMode, setIsDestinationInEditMode] = useState(false);

  // Задаем режим редактирования
  useEffect(() => {
    setIsDepartureInEditMode(isDepartureOpen);
  }, [isDepartureOpen]);

  useEffect(() => {
    setIsDestinationInEditMode(isDestinationOpen);
  }, [isDestinationOpen]);

  // Кейс показа дефолтных саджестов для пункта отправления
  useEffect(() => {
    if (
      isDepartureOpen &&
      (!isDepartureInEditMode || !departureInput) &&
      defaultDepartureSuggestions.length > 0
    ) {
      setDepartureSuggestsToShow(defaultDepartureSuggestions);
    }
  }, [isDepartureOpen, isDepartureInEditMode, departureInput, defaultDepartureSuggestions]);

  // Кейс показа дефолтных саджестов для пункта назначения
  useEffect(() => {
    if (departureId) {
      return;
    }

    if (isDestinationOpen && (!isDestinationInEditMode || !destinationInput)) {
      setDestinationSuggestsToShow(defaultDestinationSuggestions);
    }
  }, [isDestinationOpen, isDestinationInEditMode, destinationInput, departureId]);

  // Кейс показа саджеста пункта назначения на основании выбранного пункта отправления
  useEffect(() => {
    if (
      isDestinationOpen &&
      departureId &&
      (!isDestinationInEditMode || (isDestinationInEditMode && !destinationInput))
    ) {
      setDestinationSuggestsToShow(destinationSuggestionsByDepartureId);
    }
  }, [
    isDestinationOpen,
    isDestinationInEditMode,
    destinationInput,
    departureId,
    destinationSuggestionsByDepartureId,
  ]);

  // Кейс показа саджеста пункта отправления на основании значений в инпуте
  useEffect(() => {
    if (isDepartureInEditMode && departureInput) {
      setDepartureSuggestsToShow(departureSuggestions);
    }
  }, [isDepartureInEditMode, departureSuggestions, departureInput]);

  // Кейс показа саджеста пункта назначения на основании значений в инпуте
  useEffect(() => {
    if (isDestinationInEditMode && destinationInput) {
      setDestinationSuggestsToShow(destinationSuggestions);
    }
  }, [isDestinationInEditMode, destinationSuggestions, destinationInput]);

  // Небольшой костыль для избавления от фликера саджестов пункта назначения при смене пункта отправления.
  // Пояснение: когда меняем пункт отправления, в раскрытом пункте назначения на долю секунды показывается саджест,
  // соответствующий предыдущему пункту отправления. Флаги isFetching, isLoading из react-query не помогают,
  // т.к. срабатывают чуть позже, чем нужно. Поэтому просто очищаю саджесты пункта назначения при закрытии оверлея
  useEffect(() => {
    if (!isDestinationOpen) {
      setDestinationSuggestsToShow([]);
    }
  }, [isDestinationOpen]);

  // Аналогичный костыль для корректной работы статистики
  useEffect(() => {
    if (!isDepartureOpen) {
      setDepartureSuggestsToShow([]);
    }
  }, [isDepartureOpen]);

  return {
    departureSuggestionsToShow,
    destinationSuggestionsToShow,
    isDepartureInEditMode,
    isDestinationInEditMode,
  };
};

export default useSuggestionsManager;
